import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import TextField from '@material-ui/core/TextField'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { AgentMemoDepartment, GDKError, PermissionType } from '@golden/gdk-admin'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { useDialogHandleClick } from '../../../utils/default/ComplexFlowHook'
import { useDebouncedCallback } from 'use-debounce'
import { defaultEditorState, EditorState, isEmpty } from '@golden/tiptap-react'
import { TextEditor } from '../../default/form/textEditor/TextEditor'
import allRoute from '../../../components/admin/route/route'
import { useChecker } from '../../../utils/admin/AdminRouteHook'

const useStyles = makeStyles((theme) => ({
  actions: {
    paddingBottom: theme.spacing(4)
  },
  checkButtonDisabled: {
    color: `${theme.palette.action.disabled} !important`,
    border: `1px solid ${theme.palette.action.disabled} !important`
  },
  accountField: {
    [theme.breakpoints.up('sm')]: {
      position: 'relative',
      '& .MuiFormHelperText-root': {
        position: 'absolute',
        top: '100%'
      }
    }
  },
  fields: {
    padding: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      paddingBottom: theme.spacing(6)
    }
  }
}))

interface PropTypes {
  reload: (account: string) => void
}

type Department = 'RC' | 'CS'

interface CheckState {
  loading: boolean
  error: string | null
  isExist: boolean
  memo: EditorState | null
}

const initialCheckState: CheckState = {
  loading: false,
  error: null,
  isExist: false,
  memo: null
}

const apiDepartmentByLabel: Record<Department, AgentMemoDepartment> = {
  RC: 'rc',
  CS: 'cs'
}

const PlayerAgentMemoAddButton: React.FC<PropTypes> = (props) => {
  const { reload } = props
  const { t } = useT()
  const gdk = useGDK()
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const globalDialog = useGlobalDialog()
  const canEditRc = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_RC_EDIT])
  const canEditCs = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_CS_EDIT])
  const showDepartmentSelect = canEditRc && canEditCs
  const defaultDepartment = useMemo<Department | ''>(() => {
    if (showDepartmentSelect) return ''
    if (canEditRc) return 'RC'
    if (canEditCs) return 'CS'
    return ''
  }, [showDepartmentSelect, canEditRc, canEditCs])
  const [open, setOpen] = useState<boolean>(false)
  const [department, setDepartment] = useState<Department | ''>(defaultDepartment)
  const [account, setAccount] = useState<string>('')
  const [checkState, setCheckState] = useState<CheckState>(initialCheckState)
  const { loading: checkLoading, error, isExist, memo } = checkState
  const checkRequestId = useRef(0)

  const handleReset = useCallback(() => {
    checkRequestId.current += 1
    setCheckState(initialCheckState)
  }, [])
  const handleAccountChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    handleReset()
    setAccount(event.target.value)
  }, [handleReset])
  const handleDepartmentChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target
    if (value === 'RC' || value === 'CS' || value === '') {
      handleReset()
      setDepartment(value)
    }
  }, [handleReset])
  const handleMemoChange = useCallback((value: EditorState) => {
    setCheckState((state) => ({ ...state, memo: value }))
  }, [])
  const handleClose = useCallback(() => {
    setDepartment(defaultDepartment)
    setAccount('')
    handleReset()
    setOpen(false)
  }, [defaultDepartment])

  const payload = useMemo(() => ({
    account,
    department: department === '' ? department : apiDepartmentByLabel[department],
    memo: JSON.stringify(memo?.content ?? '')
  }), [account, department, memo])
  const handleSubmit = useDialogHandleClick({
    dialogId: 'addAgentMemo',
    globalDialog,
    payload,
    gdkFunc: (payload) => {
      const { department, ...memoPayload } = payload
      if (department === '') throw new Error('Department is required')
      setOpen(false)
      return gdk.player.addAgentMemo({ ...memoPayload, department })
    },
    gdkFuncDependencies: [gdk],
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('common.confirmSave')
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.saveSuccess')
    }),
    getFailDialogConfig: (error) => {
      handleClose()
      return createGlobalDialogConfig({
        variant: 'error',
        showIcon: true,
        showCancel: false,
        message: error
      })
    },
    afterSuccessDialog: () => {
      reload(account)
      handleClose()
    },
    afterSuccessDialogDependencies: [account]
  })
  const [runDebouncedCheck, cancelDebouncedCheck] = useDebouncedCallback(useCallback((requestId: number, validatePayload: { account: string, department: AgentMemoDepartment }) =>
    gdk.player.validateAgentMemoExist(validatePayload).subscribe({
      next: (data) => {
        if (requestId !== checkRequestId.current) return
        setCheckState((state) => ({
          loading: false,
          error: null,
          isExist: data.exists,
          memo: data.exists ? null : (state.memo ?? defaultEditorState)
        }))
      },
      error: (error: GDKError) => {
        if (requestId !== checkRequestId.current) return
        setCheckState({
          loading: false,
          error: error.message,
          isExist: false,
          memo: null
        })
      }
    }), [gdk]), 200)

  const handleCheck = useCallback(() => {
    if (department === '') return
    const requestId = checkRequestId.current + 1
    checkRequestId.current = requestId
    setCheckState((state) => ({ ...state, loading: true }))
    runDebouncedCheck(requestId, { account, department: apiDepartmentByLabel[department] })
  }, [account, department, runDebouncedCheck])

  useEffect(() => () => {
    checkRequestId.current += 1
    cancelDebouncedCheck()
  }, [cancelDebouncedCheck])

  const helperText = useMemo(() => {
    if (error) return error
    if (isExist) {
      return (
        <>
          <span>{t('error.duplicateMemo')}</span>
          <Link onClick={handleClose} to={allRoute.playerAgentsMemo.encodePath({ param: {}, search: { account, department: department === '' ? undefined : apiDepartmentByLabel[department], page: 1 } })}>
            {t('error.goTolink')}
          </Link>
        </>
      )
    }
    return null
  }, [error, isExist, account])

  return (
    <Box display="flex" justifyContent="flex-end">
      <Button
        className={commonClasses.purpleGradualButton}
        onClick={useCallback(() => { setOpen(true) }, [])}
      >
        {t('common.addMemo')}
      </Button>
      <Dialog open={open} fullWidth maxWidth="sm">
        <Box padding={2} className={commonClasses.dialogPinkHeader}>
          {t('common.addMemo')}
        </Box>
        <DialogContent>
          <Box className={classes.fields}>
            <Grid container spacing={2} alignItems="flex-end" justifyContent="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  className={classes.accountField}
                  label={t('common.agentsLayerAccount')}
                  placeholder={t('placeholder.inputAgentsLayerAccount')}
                  value={account}
                  onChange={handleAccountChange}
                  helperText={helperText}
                  error={error !== null}
                  fullWidth
                  required
                />
              </Grid>
              {showDepartmentSelect && (
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth required>
                    <InputLabel shrink>{t('common.department')}</InputLabel>
                    <Select
                      value={department}
                      onChange={handleDepartmentChange}
                      displayEmpty
                    >
                      <MenuItem value="">{t('common.pleaseChoose')}</MenuItem>
                      <MenuItem value="RC">RC</MenuItem>
                      <MenuItem value="CS">CS</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={3}>
                <Button
                  className={commonClasses.blueGradualOutlineButton}
                  classes={{ disabled: classes.checkButtonDisabled }}
                  disabled={department === '' || account === '' || checkLoading}
                  onClick={handleCheck}
                  fullWidth
                >
                  {t('common.check')}
                </Button>
              </Grid>
            </Grid>
          </Box>
          {memo !== null && <Box>
            <TextEditor
              value={memo}
              controls={['bold', 'ol', 'ul', 'textColor', 'clearFormat']}
              onChange={handleMemoChange}
            />
          </Box> }
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Grid container direction="row" spacing={2} justifyContent="center">
            <Grid item>
              <Button
                className={commonClasses.greyButton}
                onClick={handleClose}
              >
                {t('common.cancel')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                className={commonClasses.purpleGradualButton}
                disabled={department === '' || !memo || isEmpty(memo.content)}
                onClick={handleSubmit}
              >
                {t('common.save')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default React.memo(PlayerAgentMemoAddButton)
